import { Field, Form, Radio } from '~/components';
import React from 'react';
import CapMonthlyHoursCheckbox from './CapMonthlyHoursCheckbox';
import { calculateExpenseCost, calculateExpenseMarkup, calculateExpenseRevenue } from '~/utils';
import { useFeatures } from '~/hooks';
import styled from 'styled-components';
import { colors, weights } from '~/styles';

const TableTitle = styled.span`
  flex: 0;
  color: ${colors.grey75};
  padding-left: 0rem;
  font-size: 0.875rem;
  font-weight: ${weights.bold};
  padding-top: 1rem;
`;

function BudgetForm({ formik, project, projectModel }) {
  const features = useFeatures();

  const currency = projectModel.currency;

  const markup = calculateExpenseMarkup({
    enableMarkup: projectModel.monthlyBudgetEnableMarkup,
    markupMethod: projectModel.monthlyBudgetMarkupMethod,
    markupAmount: projectModel.monthlyBudgetMarkupAmount,
    markupRatio: projectModel.monthlyBudgetMarkupRatio,
    amount: projectModel.monthlyBudgetExpensesBillableAmount,
  });

  const totalExpenseCost = calculateExpenseCost({
    isBillable: projectModel.isBillable,
    billableAmount: projectModel.monthlyBudgetExpensesBillableAmount,
    nonBillableAmount: projectModel.monthlyBudgetExpensesNonBillableAmount,
  });

  const totalExpenseRevenue = calculateExpenseRevenue({
    markup,
    amount: projectModel.monthlyBudgetExpensesBillableAmount,
  });

  const handleExpensesBillableAmountChange = (value) => {
    // Deactivate "monthlyBudgetEnableMarkup" checkbox if billable amount is 0 or null
    if (!value) formik.setFieldValue('monthlyBudgetEnableMarkup', false);
    formik.setFieldValue('monthlyBudgetExpensesBillableAmount', value);
  };

  return (
    <>
      {projectModel.isBillable && (
        <>
          <TableTitle>Monthly Services Revenue</TableTitle>
          <Form.Control>
            <Field.Currency name="estimatedMonthlyServiceRevenue" placeholder="Amount" currency={currency} />
          </Form.Control>
        </>
      )}

      <>
        <TableTitle>Monthly Hours</TableTitle>
        {projectModel.isBillable && (
          <Form.Control>
            <Field.Number name="estimatedMonthlyBillableHours" min={0} precision={2} placeholder="Billable Hours" />
          </Form.Control>
        )}

        <Form.Control>
          <Field.Number
            name="estimatedMonthlyNonBillableHours"
            min={0}
            precision={2}
            placeholder="Non-Billable Hours"
          />
        </Form.Control>

        <Form.Control>
          <CapMonthlyHoursCheckbox />
        </Form.Control>
      </>
      <>
        <TableTitle>Monthly Expenses</TableTitle>
        {projectModel.isBillable && (
          <Form.Control>
            <Field.Currency
              onChange={handleExpensesBillableAmountChange}
              name="monthlyBudgetExpensesBillableAmount"
              placeholder="Billable Cost"
              currency={currency}
            />
          </Form.Control>
        )}

        <Form.Control>
          <Field.Currency
            name="monthlyBudgetExpensesNonBillableAmount"
            placeholder="Non-Billable Cost"
            currency={currency}
          />
        </Form.Control>

        <Form.Control>
          <Field.Currency
            name="totalExpenseCost"
            value={totalExpenseCost}
            materialPlaceholder="Total Expense Cost"
            disabled
            materialAlwaysVisible
            currency={currency}
          />
        </Form.Control>

        {(features.expenseMarkup || project.monthlyBudgetEnableMarkup) && (
          <Form.Control>
            <Field.Checkbox
              name="monthlyBudgetEnableMarkup"
              label="Markup the billable monthly expense budget amount"
              disabled={!projectModel.isBillable || !(projectModel?.monthlyBudgetExpensesBillableAmount > 0)}
            />
          </Form.Control>
        )}

        {projectModel.monthlyBudgetEnableMarkup && projectModel.isBillable && (
          <>
            <Form.Control>
              <Field.RadioGroup name="monthlyBudgetMarkupMethod">
                <Radio key="percentage" value="percentage" label="Markup by Percentage" />
                <Radio key="amount" value="amount" label="Markup by Fixed Amount" />
              </Field.RadioGroup>
            </Form.Control>

            {projectModel.monthlyBudgetMarkupMethod && (
              <Form.Control>
                {projectModel.monthlyBudgetMarkupMethod === 'amount' && (
                  <Field.Currency name="monthlyBudgetMarkupAmount" placeholder="Markup Amount" currency={currency} />
                )}

                {projectModel.monthlyBudgetMarkupMethod === 'percentage' && (
                  <Field.Number
                    suffix="%"
                    name="monthlyBudgetMarkupRatio"
                    placeholder="Markup Percentage"
                    min={0}
                    max={100}
                    precision={2}
                  />
                )}
              </Form.Control>
            )}
          </>
        )}

        <Form.Control>
          <Field.Currency
            name="totalExpenseRevenue"
            value={totalExpenseRevenue}
            materialPlaceholder="Total Budgeted to Bill"
            disabled
            materialAlwaysVisible
            currency={currency}
          />
        </Form.Control>
      </>
      {projectModel.isBillable && (
        <>
          <TableTitle>Monthly Products</TableTitle>
          <Form.Control help="The amount we charge the client for Products.">
            <Field.Currency name="monthlyBudgetProductItemsFee" placeholder="Amount" currency={currency} />
          </Form.Control>

          <TableTitle>Monthly Additional Services</TableTitle>
          <Form.Control help="The amount we charge the client for Additional Services.">
            <Field.Currency name="monthlyBudgetServiceItemsFee" placeholder="Amount" currency={currency} />
          </Form.Control>

          <TableTitle>Monthly Other Items</TableTitle>
          <Form.Control help="The amount we charge the client for Other Items.">
            <Field.Currency name="monthlyBudgetOtherItemsFee" placeholder="Amount" currency={currency} />
          </Form.Control>
        </>
      )}
    </>
  );
}

export default BudgetForm;
